import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@ridemaadi/utils';

interface FieldWrapProps {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}

function FieldWrap({ label, htmlFor, error, hint, children }: FieldWrapProps) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-content">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

const inputBase =
  'w-full min-h-touch rounded-xl border border-border bg-surface-alt px-3 text-base text-content placeholder:text-muted focus:border-primary';

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, id, className, ...rest },
  ref,
) {
  const fieldId = id ?? rest.name ?? label;
  return (
    <FieldWrap label={label} htmlFor={fieldId} error={error} hint={hint}>
      <input
        id={fieldId}
        ref={ref}
        aria-invalid={Boolean(error)}
        className={cn(inputBase, error && 'border-danger', className)}
        {...rest}
      />
    </FieldWrap>
  );
});

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  hint?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, error, hint, id, className, ...rest },
  ref,
) {
  const fieldId = id ?? rest.name ?? label;
  return (
    <FieldWrap label={label} htmlFor={fieldId} error={error} hint={hint}>
      <textarea
        id={fieldId}
        ref={ref}
        aria-invalid={Boolean(error)}
        className={cn(inputBase, 'min-h-[88px] py-2', error && 'border-danger', className)}
        {...rest}
      />
    </FieldWrap>
  );
});
